import * as T from './mediaManagementTypes';
import type { MediaManagementAction } from './mediaManagementActions';

export type { MediaManagementState } from './mediaManagementTypes';

const initialState: T.MediaManagementState = {
  locationId: null,
  data: null,
  loading: false,
  loadError: null,
  categories: [],
  categoriesLocationId: null,
  categoriesError: null,
  saving: null,
  progress: {},
  lastSave: null,
  syncBusy: false,
  toasts: [],
  access: { status: 'idle', canView: false, canEdit: false },
};

export default function mediaManagementReducer(
  state: T.MediaManagementState = initialState,
  action: MediaManagementAction,
): T.MediaManagementState {
  switch (action.type) {
    case T.OPEN_LOCATION: {
      const sameLocation = state.locationId === action.payload.locationId;
      return {
        ...initialState,
        locationId: action.payload.locationId,
        // Categories of the same outlet stay; another outlet has other items.
        categories: sameLocation ? state.categories : [],
        categoriesLocationId: sameLocation ? state.categoriesLocationId : null,
        toasts: state.toasts,
        access: state.access,
      };
    }

    case T.LOAD_MEDIA_REQUEST:
      return { ...state, loading: true, loadError: null };
    case T.LOAD_MEDIA_SUCCESS:
      if (action.payload.locationId !== state.locationId) return state;
      return { ...state, loading: false, data: action.payload.data };
    case T.LOAD_MEDIA_FAILURE:
      if (action.payload.locationId !== state.locationId) return state;
      return { ...state, loading: false, loadError: action.payload.error };

    case T.LOAD_CATEGORIES_REQUEST:
      return { ...state, categoriesError: null };
    case T.LOAD_CATEGORIES_SUCCESS:
      if (action.payload.locationId !== state.locationId) return state;
      return {
        ...state,
        categories: action.payload.categories,
        categoriesLocationId: action.payload.locationId,
        categoriesError: null,
      };
    case T.LOAD_CATEGORIES_FAILURE:
      if (action.payload.locationId !== state.locationId) return state;
      return { ...state, categoriesError: action.payload.error };

    case T.SAVE_MEDIA_REQUEST:
      return { ...state, saving: action.payload.owner };
    case T.SAVE_MEDIA_DONE:
      return { ...state, saving: null, progress: {}, lastSave: action.payload };
    case T.SET_SLOT_PROGRESS: {
      const progress = { ...state.progress };
      if (action.payload.value == null) delete progress[action.payload.key];
      else progress[action.payload.key] = action.payload.value;
      return { ...state, progress };
    }

    case T.SYNC_MEDIA_REQUEST:
      return { ...state, syncBusy: true };
    case T.SYNC_MEDIA_SUCCESS:
      return { ...state, syncBusy: false };
    case T.SYNC_MEDIA_FAILURE:
      return { ...state, syncBusy: false };

    case T.LOAD_ACCESS_REQUEST:
      // A re-read keeps the last answer on screen until the new one arrives.
      return state.access.status === 'ready' ? state : { ...state, access: { ...state.access, status: 'loading' } };
    case T.LOAD_ACCESS_DONE:
      return { ...state, access: { ...action.payload, status: 'ready' } };

    case T.ADD_TOAST:
      return { ...state, toasts: [...state.toasts, action.payload] };
    case T.DISMISS_TOAST:
      return { ...state, toasts: state.toasts.filter((t) => t.id !== action.payload) };

    default:
      return state;
  }
}
